import { IconButton } from "@mui/material";
import { AppTable } from "components/molecules";
import { ECommonDialogType } from "components/molecules/CommonDialog";
import { AlertHelper, DialogHelper, RoleHelper, TableHelper } from "helpers";
import { EAlertType } from "helpers/alertHelper";
import { useRequest } from "hooks";
import _ from "lodash";
import {
  EInprocessCheckType,
  IApiResponse,
  IPaginationRequest,
  IProductGroup,
} from "model";
import { useCallback, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { MasterDataApiService } from "services";
import { CURRENT_PAGE_SIZE, FIRST_PAGE } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { theme } from "themes";

export const ProductGroup = () => {
  const [request, setRequest] = useState<IPaginationRequest>({
    pageNumber: FIRST_PAGE,
    pageSize: CURRENT_PAGE_SIZE,
  });
  const [refresh, setRefresh] = useState<boolean>(false);
  const history = useHistory();
  const location = useLocation();
  const { data, loading } = useRequest(
    request,
    MasterDataApiService.getAllProductGroup,
    undefined,
    { refresh: refresh },
    () => {
      setRefresh(false);
    }
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const COLUMNS = [
    TableHelper.renderSTT(),
    TableHelper.createCell<IProductGroup>({
      field: "name",
      headerName: I18nTranslator(translations.name),
    }),
    TableHelper.createCell<IProductGroup>({
      field: "description",
      headerName: I18nTranslator(translations.description),
      size: "full",
    }),
    TableHelper.createCell<IProductGroup>({
      field: "isLocal",
      headerName: I18nTranslator(translations.productGroup.productionType),
      renderCell: ({ value }) => {
        return value
          ? I18nTranslator(translations.productGroup.local)
          : I18nTranslator(translations.productGroup.export);
      },
    }),
    TableHelper.createCell<IProductGroup>({
      field: "checkType",
      headerName: I18nTranslator(translations.productGroup.inprocessCheckType),
      renderCell: ({ value }) => {
        return value === EInprocessCheckType.TIME
          ? I18nTranslator(translations.productGroup.checkByTime)
          : I18nTranslator(translations.productGroup.checkByUnit);
      },
    }),
    TableHelper.createCell<IProductGroup>({
      field: "action",
      size: 128,
      headerName: I18nTranslator(translations.report.action),
      renderCell: ({ row }) => (
        <div>
          <IconButton
            disabled={!(RoleHelper.isAdmin() || RoleHelper.isSuperAdmin())}
            onClick={() => {
              history.push(`${location.pathname}/${row.id}`);
            }}
          >
            <EditIcon fontSize="small" color="primary" />
          </IconButton>
          <IconButton
            disabled={!(RoleHelper.isAdmin() || RoleHelper.isSuperAdmin())}
            onClick={() => onClickDeleteProductGroup(row)}
          >
            <DeleteIcon
              sx={{ color: theme.colors.error.light }}
              fontSize="small"
            />
          </IconButton>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    }),
  ];

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onSearchTextChange = useCallback(
    _.debounce((text: string) => {
      setRequest((prev) => ({ ...prev, searchText: text }));
    }, 150),
    []
  );

  const onPageChange = (page: number) =>
    setRequest((prev) => ({ ...prev, pageNumber: page }));
  const onPageSizeChange = (pageSize: number) =>
    setRequest((prev) => ({ ...prev, pageSize: pageSize }));

  const onClickDeleteProductGroup = (productGroup: IProductGroup) => {
    DialogHelper.open({
      title: I18nTranslator(translations.dialog.warning),
      message: I18nTranslator(translations.productGroup.deleteConfirm),
      type: ECommonDialogType.WARNING,
      onConfirm: () => deleteProductGroup(productGroup.id),
      isCancel: true,
    });
  };

  const deleteProductGroup = async (id: string) => {
    try {
      setIsLoading(true);
      const result = (await MasterDataApiService.deleteProductGroup(
        id
      )) as IApiResponse<string>;
      if (result.succeeded) {
        AlertHelper.showAlert(
          I18nTranslator(translations.productGroup.deleteSuccess),
          EAlertType.SUCCESS
        );
        setRefresh(true);
      } else {
        AlertHelper.showError(result);
      }
    } catch (error) {
      console.error("Network error", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AppTable<IProductGroup>
      title={I18nTranslator(translations.masterData.productGroup)}
      loading={loading || isLoading}
      data={{ data: data, pageNumber: 1, pageSize: 100 } || []}
      columns={COLUMNS}
      onAddNewClick={() => history.push(`${location.pathname}/new`)}
      onSearch={onSearchTextChange}
      searchPlaceholder={I18nTranslator(translations.placeholder.searchProduct)}
      hideImport
      onChangePage={onPageChange}
      removeActionColumn
      actionColumnProps={{ hideImport: true }}
      onPageSizeChange={onPageSizeChange}
      hideDownload
    />
  );
};
